'use client';
import React, { useState } from 'react';
import Link from 'next/link';

interface FooterSection {
  title: string;
  links: { label: string; href: string }[];
}

const footerSections: FooterSection[] = [
  {
    title: 'About Rani Kamlapati',
    links: [
      { label: 'About Us', href: '#about' },
      { label: 'Press & Media', href: '#press' },
      { label: 'Careers', href: '#careers' },
      { label: 'Rani Kamlapati Blog', href: '#blog' },
      { label: 'Investor Relations', href: '#investors' },
    ],
  },
  {
    title: 'Where to Find Events',
    links: [
      { label: 'Browse All Events', href: '#browse' },
      { label: 'Events Near Me', href: '#nearby' },
      { label: 'Online Events', href: '#online' },
      { label: 'Free Events', href: '#free' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', href: '#help' },
      { label: 'Contact Us', href: '#contact' },
      { label: 'Refund Policy', href: '#refunds' },
      { label: 'Buyer Protection', href: '#protection' },
      { label: 'Report an Issue', href: '#report' },
    ],
  },
  {
    title: 'Partners',
    links: [
      { label: 'Become an Organizer', href: '#organizer' },
      { label: 'Venue Partners', href: '#venues' },
      { label: 'Sponsor an Event', href: '#sponsor' },
      { label: 'API & Developers', href: '#api' },
    ],
  },
  {
    title: 'Explore',
    links: [
      { label: 'This Weekend', href: '#weekend' },
      { label: 'Community Forum', href: '#forum' },
      { label: 'Discord', href: '#discord' },
      { label: 'Event Guides', href: '#guides' },
    ],
  },
];

export default function Footer() {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const toggleSection = (title: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && agreed) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <footer style={{ background: '#1A0A00', color: '#FAF7F2', borderTop: '2px solid rgba(201,168,76,0.3)' }}>
      {/* Gold top accent line */}
      <div style={{ height: '2px', background: 'linear-gradient(to right, transparent, #C9A84C, #E8C97A, #C9A84C, transparent)' }} />

      {/* Desktop Footer */}
      <div className="hidden md:block px-12 pt-12 pb-8">
        <div className="grid grid-cols-5 gap-8 mb-12">
          {footerSections.map((section) => (
            <div key={section.title}>
              <button
                className="flex items-center justify-between w-full mb-4"
                onClick={() => toggleSection(section.title)}
              >
                <h4 className="text-sm font-medium" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif', letterSpacing: '0.3px' }}>{section.title}</h4>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className={`transition-transform duration-200 ${
                    openSections.has(section.title) ? 'rotate-180' : ''
                  }`}
                >
                  <path
                    d="M2 5L7 9L12 5"
                    stroke="#C9A84C"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm transition-colors"
                      style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'Roboto, sans-serif' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(250,247,242,0.5)')}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Accordion Footer */}
      <div className="md:hidden">
        {footerSections.map((section) => (
          <div key={section.title} style={{ borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
            <button
              onClick={() => toggleSection(section.title)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="text-sm font-medium" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>{section.title}</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 ${
                  openSections.has(section.title) ? 'rotate-180' : ''
                }`}
              >
                <path
                  d="M2 5L7 9L12 5"
                  stroke="#C9A84C"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {openSections.has(section.title) && (
              <div className="px-5 pb-4">
                {section.links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="block py-2 text-sm transition-colors"
                    style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'Roboto, sans-serif' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(250,247,242,0.5)')}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Newsletter + Social */}
      <div className="px-5 md:px-12 py-8" style={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          {/* Newsletter */}
          <div className="flex-1 max-w-md">
            <h4 className="text-base font-bold mb-1" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>Subscribe</h4>
            <p className="text-sm mb-4" style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'Roboto, sans-serif' }}>Get the latest events and updates from Rani Kamlapati</p>
            {subscribed ? (
              <p className="text-sm" style={{ color: '#C9A84C' }}>Thanks for subscribing! ✨</p>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Your Email"
                    className="flex-1 h-10 px-4 text-sm outline-none transition-colors"
                    style={{
                      background: 'rgba(201,168,76,0.08)',
                      color: '#FAF7F2',
                      border: '1px solid rgba(201,168,76,0.25)',
                      borderRadius: '8px',
                      fontFamily: 'Roboto, sans-serif',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.6)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.25)')}
                  />
                  <button
                    onClick={handleSubscribe}
                    className="h-10 w-10 flex items-center justify-center transition-all duration-200 hover:opacity-90 flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', borderRadius: '8px' }}
                    aria-label="Subscribe"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8H13M13 8L9 4M13 8L9 12"
                        stroke="#FAF7F2"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <div
                    onClick={() => setAgreed(!agreed)}
                    className="w-4 h-4 mt-0.5 flex-shrink-0 flex items-center justify-center transition-colors"
                    style={{
                      background: agreed ? 'linear-gradient(135deg, #C9A84C, #8B6914)' : 'transparent',
                      border: `1px solid ${agreed ? '#C9A84C' : 'rgba(201,168,76,0.3)'}`,
                      borderRadius: '3px',
                    }}
                  >
                    {agreed && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="#FAF7F2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs leading-relaxed" style={{ color: 'rgba(250,247,242,0.45)', fontFamily: 'Roboto, sans-serif' }}>
                    I agree with the{' '}
                    <Link href="#privacy" className="underline" style={{ color: 'rgba(201,168,76,0.7)' }}>
                      Privacy Policy
                    </Link>{' '}
                    and I&apos;d like to receive the latest Rani Kamlapati news and deals by email.
                  </span>
                </label>
              </>
            )}
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {[
              {
                name: 'TikTok',
                href: '#tiktok',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M12 2C12 4.5 14 6 16 6V9C14.5 9 13 8.5 12 7.5V13C12 15.21 10.21 17 8 17C5.79 17 4 15.21 4 13C4 10.79 5.79 9 8 9V12C7.45 12 7 12.45 7 13C7 13.55 7.45 14 8 14C8.55 14 9 13.55 9 13V2H12Z"
                      stroke="#C9A84C"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                name: 'YouTube',
                href: '#youtube',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <rect x="1" y="4" width="16" height="10" rx="3" stroke="#C9A84C" strokeWidth="1.2" />
                    <path d="M7 7L12 9L7 11V7Z" fill="#C9A84C" />
                  </svg>
                ),
              },
              {
                name: 'Twitter',
                href: '#twitter',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M2 2L8.5 9.5M8.5 9.5L2 16H6L9 12.5L12 16H16L9.5 8.5M8.5 9.5L16 2H12L9 5.5L6 2H2"
                      stroke="#C9A84C"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                name: 'Facebook',
                href: '#facebook',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M10 2H12V5H10C9.45 5 9 5.45 9 6V8H12L11.5 11H9V16H6V11H4V8H6V6C6 3.79 7.79 2 10 2Z"
                      stroke="#C9A84C"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                name: 'Instagram',
                href: '#instagram',
                icon: (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <rect x="2" y="2" width="14" height="14" rx="4" stroke="#C9A84C" strokeWidth="1.2" />
                    <circle cx="9" cy="9" r="3.5" stroke="#C9A84C" strokeWidth="1.2" />
                    <circle cx="13" cy="5" r="1" fill="#C9A84C" />
                  </svg>
                ),
              },
            ].map((social) => (
              <Link
                key={social.name}
                href={social.href}
                className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
                style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}
                aria-label={social.name}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.1)')}
              >
                {social.icon}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Region + Legal */}
      <div className="px-5 md:px-12 py-4" style={{ borderTop: '1px solid rgba(201,168,76,0.12)' }}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Region Selector */}
          <button
            className="flex items-center gap-2 text-xs transition-colors"
            style={{ color: 'rgba(250,247,242,0.4)', fontFamily: 'Roboto, sans-serif' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(250,247,242,0.4)')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1" />
              <path d="M7 1C7 1 4 4 4 7C4 10 7 13 7 13" stroke="currentColor" strokeWidth="1" />
              <path d="M7 1C7 1 10 4 10 7C10 10 7 13 7 13" stroke="currentColor" strokeWidth="1" />
              <path d="M1 7H13" stroke="currentColor" strokeWidth="1" />
            </svg>
            Global (English)
          </button>

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              'User Service Agreement',
              'Privacy Policy',
              'Cookie Settings',
              'Cookie Policy',
            ].map((item, i) => (
              <React.Fragment key={item}>
                {i > 0 && <span style={{ color: 'rgba(201,168,76,0.2)' }} className="text-xs">|</span>}
                <Link href="#" className="text-xs transition-colors" style={{ color: 'rgba(250,247,242,0.35)', fontFamily: 'Roboto, sans-serif' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(250,247,242,0.35)')}
                >
                  {item}
                </Link>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="px-5 md:px-12 py-4" style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
        <p className="text-xs text-center md:text-left" style={{ color: 'rgba(250,247,242,0.25)', fontFamily: 'Roboto, sans-serif' }}>
          Copyright &copy; 2025 Rani Kamlapati. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
